'use client';

import React, { useState, useRef } from 'react';
import NextImage from 'next/image';
import Head from 'next/head';
import { HowItWorks } from '@/components/HowItWorks';
import { FAQ } from '@/components/FAQ';
import { RecommendedArticles } from '@/components/RecommendedArticles';
import { Footer } from '@/components/Footer';
import { ResultCard } from '@/components/ResultCard';
import { UserTestimonials } from '@/components/UserTestimonials';
import { AttractivenessRecommendations } from '@/components/AttractivenessRecommendations';
import { FacialScienceVideo } from '@/components/FacialScienceVideo';

import MaleAttractivenessResult from '@/components/MaleAttractivenessResult';
import { MaleAttractivenessData } from '@/lib/maleAttractivenessReportGenerator';

// Gradient Button Component
const GradientButton = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-semibold hover:from-blue-700 hover:to-purple-700 cursor-pointer transition-all duration-300 transform hover:-translate-y-1 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
  >
    {children}
  </button>
);

// Male User Stories Data
const maleStories = [
  {
    avatar: '/images/Marcus.png',
    content: `Ten-Year Crush: I ran the male attractiveness test on every photo I had, picked the one that scored 9.1, and used it as my dating-app pic. The girl I'd loved since high school finally swiped right; one coffee later she said, "I always hoped you'd notice me." Now we're planning our trip to Bali.`,
    name: 'Marcus, 28',
    job: 'Software Engineer',
  },
  {
    avatar: '/images/David.png',
    content: `Side-Hustle Boost: After my selfie jumped from 6.4 to 8.8 on the male attractiveness test, I posted the new headshot on IG. Followers tripled, brands slid into my DMs, and my affiliate links earned me an extra $3k last month—just for looking sharper.`,
    name: 'David, 35',
    job: 'Marketing Director',
  },
  {
    avatar: '/images/Jake.png',
    content: `Pride & Power: Curious about my appeal to men, I uploaded a gym mirror pic to the male attractiveness test. The 8.5 "same-sex magnetism" score convinced me to swap it into my Grindr profile. Matches flooded in, and one 6′4″ lawyer asked me out—best steak dinner I've had in years.`,
    name: 'Jake, 24',
    job: 'Fitness Trainer',
  },
];

// User Stories Component for Male Users (Carousel Design)
const UserStories: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const total = maleStories.length;

  const prev = () => setCurrent((prev) => (prev - 1 + total) % total);
  const next = () => setCurrent((prev) => (prev + 1) % total);

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8" style={{ minHeight: '350px' }}>
      {/* 主要内容区域 */}
      <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8 mb-8">
        {/* 头像 */}
        <div className="flex-shrink-0 flex flex-col items-center w-full md:w-auto">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gray-200 border-4 border-white overflow-hidden flex items-center justify-center shadow-lg">
            <img src={maleStories[current].avatar} alt={maleStories[current].name} className="w-full h-full object-cover" />
          </div>
        </div>
        {/* 内容区域 */}
        <div className="flex-grow flex flex-col items-center md:items-start">
          <p className="text-base md:text-lg text-gray-700 font-medium mb-4 italic text-center md:text-left">"{maleStories[current].content}"</p>
          <div className="text-center md:text-left">
            <span className="font-bold text-gray-900">{maleStories[current].name}</span>
            <span className="ml-2 text-gray-500">- {maleStories[current].job}</span>
          </div>
        </div>
      </div>

      {/* 底部控制区域 - 独立于上方布局 */}
      <div className="flex flex-col items-center gap-4">
        {/* 分页指示器 */}
        <div className="flex gap-2 justify-center">
          {maleStories.map((_, idx) => (
            <span key={idx} className={`w-3 h-3 rounded-full ${idx === current ? 'bg-purple-500' : 'bg-gray-300'} inline-block`}></span>
          ))}
        </div>
        {/* 导航按钮 */}
        <div className="flex gap-4 justify-center">
          <button onClick={prev} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button onClick={next} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default function MaleAttractivenessTest() {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [result, setResult] = useState<MaleAttractivenessData | null>(null);
  const [resultImageUrl, setResultImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }

    setError(null);
    setLoading(true);
    setResult(null);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target?.result as string;

        try {
          // Send to male attractiveness API
          const response = await fetch('/api/male-attractiveness', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ image: base64Data }),
          });

          const data = await response.json();

          if (data.error) {
            // 处理性别不匹配的特殊情况
            if (data.error === 'GENDER_MISMATCH') {
              const detectedGender = data.detectedGender || 'non-male';
              if (detectedGender === 'Female') {
                setError('🚹 Female face detected! This is a Male Attractiveness Test. Please upload a photo of a male person to get accurate masculine appeal analysis.');
              } else {
                setError('🚹 Unable to detect a male face! This test requires a clear photo of a male person. Please try uploading a different photo with better lighting and a clearly visible male face.');
              }
            } else {
              setError(data.message || data.error);
            }
            setPreviewUrl(base64Data);
          } else {
            setResult(data);
            setResultImageUrl(base64Data);
            setPreviewUrl('');
          }
        } catch (err) {
          setError('Failed to analyze image. Please try again.');
          setPreviewUrl(base64Data);
        } finally {
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read image file');
        setLoading(false);
      };

      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to process image. Please try again.');
      setLoading(false);
    }
  };



  const handleRetry = () => {
    setResult(null);
    setResultImageUrl('');
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };



  return (
    <>
      <Head>
        <link rel="canonical" href="https://www.aiattractiveness.com/male-attractiveness-test" />
      </Head>
      <main className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-blue-50">
          <div className="container mx-auto max-w-7xl px-6 py-16 md:py-20">

            {/* Main Title Section */}
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-800 leading-tight">
                Male Attractiveness Test
              </h1>
              <p className="mt-4 text-xl text-gray-600">
                Discover Your Masculine Appeal Instantly
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-16 items-center">

              {/* Left Content */}
              <div className="lg:col-span-5 space-y-2 lg:space-y-8 text-center lg:text-left">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                  Show your male hormones
                </h2>

                {/* Image - 在移动端显示在标题下面 */}
                <div className="lg:hidden flex justify-center mt-3 mb-2">
                  <NextImage
                    src="/images/male-attractiveness-test-visualization.jpg"
                    alt="Male Attractiveness Test Visualization"
                    width={320}
                    height={320}
                    className="rounded-lg object-cover shadow-2xl"
                    priority
                  />
                </div>

                <p className="text-lg text-gray-600 leading-relaxed">
                  The most attractive thing in a man is how happy and passionate he is.
                </p>
                <div className="pt-4">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    name="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={loading}
                  />

                  {/* Upload button */}
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={loading}
                    className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg font-semibold hover:from-blue-700 hover:to-purple-700 cursor-pointer transition-all duration-300 transform hover:-translate-y-1 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed w-full max-w-xs mx-auto md:w-auto md:max-w-none"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      'Upload your image'
                    )}
                  </button>

                  {/* Error message */}
                  {error && (
                    <div className={`mt-4 p-4 rounded-lg text-center ${
                      error.includes('🚹')
                        ? 'bg-blue-50 border border-blue-200'
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className={`text-sm ${
                        error.includes('🚹')
                          ? 'text-blue-700'
                          : 'text-red-600'
                      }`}>
                        {error}
                      </p>
                      {error.includes('🚹') && (
                        <p className="text-xs text-blue-600 mt-2">
                          💡 Tip: Make sure the photo clearly shows a male face with good lighting
                        </p>
                      )}
                    </div>
                  )}

                  {/* Privacy notice */}
                  <p className="mt-4 text-sm text-gray-500 flex items-center justify-center lg:justify-start">
                    <svg className="w-4 h-4 mr-2 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Photos are processed server-side. We don't save or share them.
                  </p>
                </div>
              </div>

              {/* Right Content - Image (桌面端显示) */}
              <div className="hidden lg:flex lg:col-span-7 justify-center lg:justify-end">
                <NextImage
                  src="/images/male-attractiveness-test-visualization.jpg"
                  alt="Male Attractiveness Test Visualization"
                  width={700}
                  height={700}
                  className="rounded-lg object-cover shadow-2xl"
                  priority
                />
              </div>

            </div>
          </div>
        </section>

        {/* Results Section - Show immediately after hero section */}
        {result && resultImageUrl && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6">
              <MaleAttractivenessResult
                data={result}
                imageUrl={resultImageUrl}
                onRetry={handleRetry}
              />
            </div>
          </section>
        )}

        {/* Upload Section - Show preview when uploading but no result yet */}
        {previewUrl && !result && (
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="text-center">
                    <div className="relative w-64 h-64 mx-auto rounded-xl overflow-hidden mb-6">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {error && (
                      <div className={`text-center p-4 rounded-lg ${
                        error.includes('🚹')
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-red-50 text-red-600'
                      }`}>
                        <p className="font-medium">{error}</p>
                        {error.includes('🚹') && (
                          <div className="mt-3">
                            <button
                              onClick={handleUploadClick}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              📷 Upload Male Photo
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 7 Male Indicators Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="container mx-auto max-w-7xl px-6">

            {/* Section Title */}
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                Male Attractiveness Test — Our Analysis Results
              </h2>
              </div>

            <div className="flex flex-col md:flex-row gap-16 items-center">
              {/* Left - Content */}
              <div className="order-2 md:order-1 flex-1 space-y-6">
                <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  7 Key Dimensions of Male Attractiveness Analysis
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our comprehensive male attractiveness test analyzes 7 essential dimensions that define masculine appeal. Each dimension provides detailed insights with personalized feedback, impact assessment, and actionable improvement tips.
                </p>
                <ul className="list-disc pl-6 text-base text-gray-700 space-y-2">
                  <li><strong>Overall Attractiveness</strong> - Primary factor in masculine appeal with comprehensive scoring</li>
                  <li><strong>Masculinity Index</strong> - Defines masculine presence and appeal characteristics</li>
                  <li><strong>Jawline Strength</strong> - Key masculine facial feature analysis and definition assessment</li>
                  <li><strong>Facial Symmetry</strong> - Foundation of facial attractiveness with balance evaluation</li>
                  <li><strong>Skin Quality</strong> - Overall appearance and health indicator assessment</li>
                  <li><strong>Smile Attractiveness</strong> - Social appeal and approachability factor analysis</li>
                  <li><strong>Male Attractiveness Score</strong> - Comprehensive masculine appeal rating</li>
                </ul>
                <div className="pt-4 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
              </div>

              {/* Right - Image */}
              <div className="order-1 md:order-2 flex-1 flex justify-center">
                <NextImage
                  src="/images/male-attractiveness-test-result-indicators.jpg"
                  alt="Male Attractiveness Test Result Indicators"
                  width={700}
                  height={700}
                  className="rounded-lg object-cover shadow-2xl"
                  priority
                />
              </div>
            </div>
          </div>
        </section>

        {/* What can Male AI Attractiveness Test do? Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="container mx-auto max-w-7xl px-6">

            {/* Section Title */}
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                What can Male AI Attractiveness Test do?
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Our powerful male attractiveness test can be widely used to enhance your masculine appeal and confidence.
              </p>
            </div>

            {/* Sub-section 1: Dating Success */}
            <div className="flex flex-col md:flex-row gap-16 items-center mb-24">
              <div className="order-1 md:order-1 flex-1 flex justify-center">
                <NextImage
                  src="/images/male-dating-profile-optimization.jpg"
                  alt="Male Dating Profile Optimization"
                  width={630}
                  height={360}
                  className="rounded-lg object-cover shadow-2xl"
                />
              </div>
              <div className="order-2 md:order-2 flex-1 space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Dating Profile Optimization for Men</h3>
                <p className="text-gray-600">
                  Maximize your dating success with our specialized male attractiveness analysis that evaluates masculine appeal factors. Our AI-powered male beauty test analyzes jawline strength, facial masculinity, and grooming quality to help you select the most attractive photos for your dating profile. Men using our male attractiveness test report significantly improved match rates and better first impressions on dating apps.
                </p>
                <div className="pt-2 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
              </div>
            </div>

            {/* Sub-section 2: Professional Image */}
            <div className="flex flex-col md:flex-row gap-16 items-center mb-24">
              <div className="order-1 md:order-2 flex-1 flex justify-center">
                <NextImage
                  src="/images/male-professional-image-enhancement.jpg"
                  alt="Professional Male Image Enhancement"
                  width={630}
                  height={360}
                  className="rounded-lg object-cover shadow-2xl"
                />
              </div>
              <div className="order-2 md:order-1 flex-1 space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Professional Image Enhancement</h3>
                <p className="text-gray-600">
                  Elevate your professional presence with our comprehensive male attractiveness assessment designed for career success. Our advanced male beauty analysis evaluates confidence projection, grooming standards, and professional appeal factors that matter in business environments. Whether you're updating your LinkedIn profile or building your personal brand, our male attractiveness test provides actionable insights.
                </p>
                <div className="pt-2 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
                </div>
              </div>

            {/* Sub-section 3: Appeal to Men */}
            <div className="flex flex-col md:flex-row gap-16 items-center">
              <div className="order-1 md:order-1 flex-1 flex justify-center">
                <NextImage
                  src="/images/appeal-to-men.jpg"
                  alt="Appeal to Men - Same-Sex Magnetism"
                  width={630}
                  height={360}
                  className="rounded-lg object-cover shadow-2xl"
                />
                  </div>
              <div className="order-2 md:order-2 flex-1 space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Appeal to Men</h3>
                <p className="text-gray-600">
                  Whether you're on Grindr or just curious, the test also rates same-sex magnetism. See which pic screams "confident alpha" to other guys and update your profile accordingly.
                </p>
                <div className="pt-2 flex justify-center md:justify-start">
                  <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    Try it now
                  </GradientButton>
                </div>
              </div>
            </div>

            {/* Male User Stories Sub-section */}
            <div className="pt-24">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
                  User stories of Male Attractiveness Test
                </h2>
                <p className="mt-4 text-lg text-gray-600">
                  Real experiences from men who improved their attractiveness and confidence
                </p>
              </div>
              <UserStories />
            </div>

          </div>
        </section>



        {/* FAQ Section */}
        <FAQ
          title="FAQs of Male Attractiveness Test"
          faqs={[
            {
              question: 'How does the male attractiveness test differ from general tests?',
              answer: `Our male attractiveness test is specifically calibrated for masculine features. It analyzes jawline strength, facial masculinity, bone structure, and other characteristics that are particularly valued in male beauty standards, providing more accurate results for men.`
            },
            {
              question: 'What facial features are most important for male attractiveness?',
              answer: `Key features for male attractiveness include a strong jawline, defined cheekbones, prominent brow ridge, clear skin, and well-groomed facial hair. Facial symmetry, proportion, and overall masculine appeal are also crucial factors in our analysis.`
            },
            {
              question: 'Can facial hair affect my male attractiveness test score?',
              answer: `Yes, facial hair significantly impacts your male attractiveness test results. Well-groomed facial hair that suits your face shape can enhance your score, while poor grooming may detract from it. Our AI analyzes grooming quality and style appropriateness.`
            },
            {
              question: 'How accurate is the male attractiveness test?',
              answer: `Our male attractiveness test is trained specifically on masculine beauty standards and provides accurate analysis based on established criteria for male facial attractiveness. Results are objective and based on scientific principles of male appeal.`
            },
            {
              question: 'Is the male attractiveness test private and safe?',
              answer: `Absolutely. Your privacy is 100% protected. We do not save, store, or share any image you upload for the male attractiveness test. All processing is done securely and your photos are deleted immediately after analysis.`
            },
            {
              question: 'Still have questions?',
              answer: `Reach out via the <a href="/contact" class="text-blue-600 underline hover:text-blue-800">Contact Us</a> page—our support team is ready to help with any other inquiries about the best male attractiveness test online.`
            }
          ]}
        />

        {/* How to Use Our Male Attractiveness Test Step by Step? */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How to Use Our Male Attractiveness Test Step by Step?
              </h2>
            </div>
            <div className="flex flex-col md:flex-row gap-12 max-w-4xl mx-auto items-center">
              {/* 右侧图片（移动端在上） */}
              <div className="order-1 md:order-2 flex-1 flex justify-center mb-8 md:mb-0">
                <NextImage
                  src="/images/male-upload-image.jpg"
                  alt="How to Use Male Attractiveness Test"
                  width={400}
                  height={400}
                  className="rounded-xl object-cover shadow-2xl"
                />
              </div>
              {/* 左侧步骤 */}
              <div className="order-2 md:order-1 flex-1 space-y-10">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">1</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 1: Upload Your Photo</h3>
                    <p className="text-gray-600">Hit the upload button and add a clear, front-facing image. No sign-up needed—this free male attractiveness test starts the moment your photo arrives.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">2</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 2: Get Your Male Attractiveness Score</h3>
                    <p className="text-gray-600">In seconds our specialized male attractiveness test delivers a 1–10 masculine appeal score plus detailed metrics like jawline strength, facial masculinity, and grooming quality—everything you need to enhance your male attractiveness.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 relative bg-gradient-to-br from-slate-800/95 via-gray-700/90 to-blue-900/85 rounded-3xl mx-4">
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-4xl font-bold mb-6 text-white drop-shadow-lg">
              Ready to discover your Male Attractiveness score?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90 text-white/90">
              Join thousands of men who trust our specialized male attractiveness test for accurate masculine appeal analysis
            </p>
            <GradientButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Try it now
            </GradientButton>
          </div>
          {/* 渐变光圈装饰 */}
          <div className="absolute left-1/2 top-3/4 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[120px] bg-gradient-to-r from-slate-600/30 via-gray-500/20 to-blue-700/15 rounded-full blur-2xl opacity-70 z-0"></div>
        </section>

        <Footer />
      </main>
    </>
  );
}